import React, { useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import { Button, Chip, MenuItem, Stack, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { defaultVariables } from "../../../../config";
import { TableCount } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { tableIcons } from "../../../../config/design/TableIcons";
import { currencySymbol, price, showTableDate } from "../../../../config/values";
import AppAnimate from "../../../components/AppAnimate";
import Title from "../../../components/elements/Title";
import RayaField from "../../../components/elements/fields/RayaField";
import { Download, RefreshCw } from "lucide-react";

function VendorSettlementsPage({ classes }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const tableRef = useRef();
    const [colorFilter, setColorFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [busy, setBusy] = useState(false);

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    const reload = () => tableRef.current && tableRef.current.onQueryChange();

    const colorChip = (row) => {
        const color = row.color_status || "yellow";
        const map = {
            green: { label: t("settlementColorGreen"), sx: { bgcolor: "#2e7d32", color: "#fff" } },
            yellow: { label: t("settlementColorYellow"), sx: { bgcolor: "#f9a825", color: "#111" } },
            red: { label: t("settlementColorRed"), sx: { bgcolor: "#c62828", color: "#fff" } },
        };
        const item = map[color] || map.yellow;
        return <Chip size="small" label={item.label} sx={item.sx} />;
    };

    const markPaid = async (row, paid = true) => {
        setBusy(true);
        try {
            await axios.post(
                `${defaultVariables.url}/einvoices/settlements/${paid ? "mark-paid" : "mark-unpaid"}`,
                { id: row.id },
                config
            );
            enqueueSnackbar(t("settlementActionSuccess"), { variant: "success" });
            reload();
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("settlementActionFailed"), { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const downloadSettlementInvoices = async (row) => {
        setBusy(true);
        try {
            const res = await axios.get(
                `${defaultVariables.url}/einvoices/list?shop_id=${row.shop_id}&type=vendor_commission&per_page=100`,
                config
            );
            const rows = res.data?.data || [];
            const forDay = rows.filter((inv) => {
                if (!inv.sent_at && !inv.created_at) return false;
                const d = (inv.sent_at || inv.created_at || "").slice(0, 10);
                const sd = (row.settlement_date || "").slice(0, 10);
                return d === sd || !sd;
            });
            if (!forDay.length) {
                enqueueSnackbar(t("settlementNoInvoices"), { variant: "warning" });
                return;
            }
            for (const inv of forDay.slice(0, 20)) {
                const file = await axios.get(`${defaultVariables.url}/einvoices/download/${inv.id}`, {
                    ...config,
                    responseType: "blob",
                    params: { format: "pdf" },
                });
                const contentType = file.headers["content-type"] || "application/pdf";
                const disposition = file.headers["content-disposition"] || "";
                let filename = `${inv.invoice_no || inv.id}.pdf`;
                const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, "");
                } else if (contentType.includes("xml")) {
                    filename = `${inv.invoice_no || inv.id}.xml`;
                }
                const url = window.URL.createObjectURL(new Blob([file.data], { type: contentType }));
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("settlementActionFailed"), { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const columns = [
        {
            title: t("settlementDate"),
            field: "settlement_date",
            render: (row) => (
                <Typography className={classes.dateTable}>{showTableDate(row.settlement_date)}</Typography>
            ),
        },
        {
            title: t("shop"),
            field: "shop.name",
            render: (row) => row.shop?.name || "-",
        },
        {
            title: t("ordersCount"),
            field: "orders_count",
        },
        {
            title: t("settlementGross"),
            field: "gross_total",
            render: (row) => `${price(row.gross_total)} ${currencySymbol}`,
        },
        {
            title: t("settlementCommission"),
            field: "commission_total",
            render: (row) => `${price(row.commission_total)} ${currencySymbol}`,
        },
        {
            title: t("settlementPayout"),
            field: "payout_total",
            render: (row) => (
                <strong>
                    {price(row.payout_total)} {currencySymbol}
                </strong>
            ),
        },
        {
            title: t("status"),
            field: "color_status",
            render: (row) => colorChip(row),
        },
        {
            title: t("actions"),
            sorting: false,
            render: (row) => (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {row.status !== "paid" ? (
                        <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => markPaid(row, true)}>
                            {t("settlementMarkPaid")}
                        </Button>
                    ) : (
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => markPaid(row, false)}>
                            {t("settlementMarkUnpaid")}
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download size={14} />}
                        disabled={busy}
                        onClick={() => downloadSettlementInvoices(row)}
                    >
                        {t("downloadInvoice")}
                    </Button>
                </Stack>
            ),
        },
    ];

    return (
        <AppAnimate animation="transition.slideUpIn" delay={200} title={t("vendorSettlementsTitle")}>
            <Grid container spacing={2}>
                <Grid item size={12} className="flex-space">
                    <div>
                        <Title>{t("vendorSettlementsTitle")}</Title>
                        <Typography variant="body2" color="text.secondary">
                            {t("vendorSettlementsDesc")}
                        </Typography>
                    </div>
                    <Button startIcon={<RefreshCw size={16} />} onClick={reload} disabled={busy}>
                        {t("refresh")}
                    </Button>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                    <RayaField
                        select
                        fullWidth
                        label={t("status")}
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setTimeout(reload, 0);
                        }}
                    >
                        <MenuItem value="all">{t("all")}</MenuItem>
                        <MenuItem value="unpaid">{t("settlementUnpaid")}</MenuItem>
                        <MenuItem value="paid">{t("settlementPaid")}</MenuItem>
                    </RayaField>
                </Grid>
                <Grid item size={{ xs: 12, md: 3 }}>
                    <RayaField
                        select
                        fullWidth
                        label={t("settlementColor")}
                        value={colorFilter}
                        onChange={(e) => {
                            setColorFilter(e.target.value);
                            setTimeout(reload, 0);
                        }}
                    >
                        <MenuItem value="all">{t("all")}</MenuItem>
                        <MenuItem value="yellow">{t("settlementColorYellow")}</MenuItem>
                        <MenuItem value="red">{t("settlementColorRed")}</MenuItem>
                        <MenuItem value="green">{t("settlementColorGreen")}</MenuItem>
                    </RayaField>
                </Grid>
                <Grid item size={12}>
                    <MaterialTable
                        tableRef={tableRef}
                        icons={tableIcons}
                        title=""
                        columns={columns}
                        localization={getDataTableLocalization()}
                        options={{
                            pageSize: TableCount,
                            pageSizeOptions: [TableCount, 24, 48],
                            search: true,
                            sorting: true,
                            actionsColumnIndex: -1,
                        }}
                        data={(query) =>
                            new Promise((resolve, reject) => {
                                const page = query.page + 1;
                                const search = query.search || "";
                                axios
                                    .get(
                                        `${defaultVariables.url}/einvoices/settlements?page=${page}&per_page=${query.pageSize}&search=${encodeURIComponent(
                                            search
                                        )}&status=${statusFilter}&color=${colorFilter}`,
                                        config
                                    )
                                    .then((res) => {
                                        resolve({
                                            data: res.data.data || [],
                                            page: (res.data.current_page || 1) - 1,
                                            totalCount: res.data.total || 0,
                                        });
                                    })
                                    .catch(reject);
                            })
                        }
                    />
                </Grid>
            </Grid>
        </AppAnimate>
    );
}

export default withStyles(Styles)(VendorSettlementsPage);
